import { useEffect, useMemo, useState } from 'react'
import CLUB from '../../config/club.js'
import {
  getMemberId, onMemberChange, watchMyGames, loadMemberGame,
  saveMemberSeason, updateMemberSeason,
} from '../../lib/memberGgc.js'
import { seasonStandings, potPayout, DEFAULT_PARS } from './ggSeasonMode.js'

// Members' SEASON mode — its own entry (reached only from the Season tile).
//
// Styling MATCHES the shared game-setup form (GGameSetup) EXACTLY: the outer
// `.card` panel, WHITE inputs (`border rounded px-3 py-2 text-base` — NOT the
// `.input` class, which is cream under .pbcc-premium), white sub-panels
// (`rounded-lg border p-3`, border greens when active), dark readable labels
// (`text-lg font-semibold text-gray-800` / `text-sm font-bold uppercase
// tracking-wide text-gray-500`), `font-bold text-xl` headers, the icon
// check-to-expand side-bet rows (💰 Scats / 💰 Skins / 💠 Dots), and the shared
// .btn-primary / .btn-ghost buttons. No cream-on-cream, no light-gold labels.
const GREEN = CLUB.primaryColor || 'var(--ge-primary)'
const INP = 'border rounded px-3 py-2 text-base'          // white input (game-setup standard)
const INPC = 'border rounded px-2 py-1.5 text-base'       // compact white input
const SUBLABEL = 'text-sm font-bold uppercase tracking-wide text-gray-500'
const PANEL = { borderColor: '#E5E7EB' }

let _seq = 0
const nid = (p) => `${p}${Date.now().toString(36)}${(_seq++).toString(36)}`
const blankPlayer = () => ({ playerId: nid('p'), name: '', ch: '' })
const blankTeam = (n) => ({ id: nid('t'), name: `Team ${n}`, players: [blankPlayer(), blankPlayer()] })

const SEASON_TYPES = [
  { key: 'team-stableford', icon: '🎯', label: 'Team Stableford Season',
    desc: '2-man teams scored best-ball Stableford (net). Any number of teams; optionally combine them into bigger sides.' },
  { key: 'team-cup', icon: '🏆', label: 'Team Cup Season',
    desc: 'Multi-match Team Cup — best-ball hole points, combined across every round, pot at the end.' },
]

const SIDE_BETS = [
  { key: 'scats', icon: '💰', label: 'Scats (side pot)', border: '#1B7A3E', desc: 'Side pot — splits by birdies-or-better at the end. Settles zero-sum on the results screen.' },
  { key: 'skins', icon: '💰', label: 'Skins (side game)', border: '#B8860B', desc: 'Low score wins the hole; ties carry over. $/skin split-pot alongside the season game.' },
  { key: 'dots', icon: '💠', label: 'Dots (garbage / junk)', border: '#7C3AED', desc: 'Points for greenies, sandies, birdies and more — tapped on the scorecard each hole.' },
]

// Icon check-to-expand side-bet rows — matches GGameSetup's Scats/Skins/Dots.
function SideBetRows({ value, onChange }) {
  return (
    <div className="space-y-2">
      {SIDE_BETS.map((b) => {
        const on = !!value?.[b.key]
        return (
          <div key={b.key} className="rounded-lg border p-3" style={{ borderColor: on ? b.border : '#E5E7EB' }}>
            <label className="flex items-center gap-2 text-lg font-bold text-gray-800">
              <input type="checkbox" checked={on} onChange={(e) => onChange({ ...value, [b.key]: e.target.checked })} /> {b.icon} {b.label}
            </label>
            {on && <p className="text-sm text-gray-600 mt-2">{b.desc}</p>}
          </div>
        )
      })}
    </div>
  )
}

function normalizeSeason(meta) {
  return {
    name: meta?.name || 'Season',
    seasonType: meta?.seasonType || 'team-stableford',
    net: meta?.net !== false,
    teams: meta?.teams || [],
    sides: meta?.sides || [],
    rounds: meta?.rounds || [],
    pot: meta?.pot || { total: 0, payout: 'winner', n: 3, useSides: false },
    sideBets: meta?.sideBets || { scats: false, skins: false, dots: false },
    targetRounds: meta?.targetRounds || 0,
  }
}

export default function GSeasonMode() {
  const [memberId, setMemberId] = useState(() => { try { return getMemberId() } catch { return null } })
  useEffect(() => {
    let off
    try { off = onMemberChange(setMemberId) } catch { /* auth unavailable */ }
    return () => { try { off && off() } catch { /* noop */ } }
  }, [])

  const [view, setView] = useState('list')       // list | setup | season
  const [seasons, setSeasons] = useState([])
  const [current, setCurrent] = useState(null)   // { id, meta }

  useEffect(() => {
    if (!memberId) { setSeasons([]); return undefined }
    return watchMyGames((games) => setSeasons(games.filter((g) => g.kind === 'season')))
  }, [memberId])

  const openSeason = async (id) => {
    const rec = await loadMemberGame(id)
    if (rec?.meta) { setCurrent({ id, meta: normalizeSeason(rec.meta) }); setView('season') }
  }
  const saveCurrent = async (meta) => {
    if (!current?.id) return
    await updateMemberSeason(current.id, meta)
    setCurrent({ id: current.id, meta })
  }

  if (!memberId) {
    return <div className="card"><p className="text-base text-gray-700">Sign in to set up a season.</p></div>
  }
  if (view === 'setup') return <SeasonSetup onCancel={() => setView('list')} onCreated={openSeason} />
  if (view === 'season' && current) return <SeasonView season={current.meta} onBack={() => { setView('list'); setCurrent(null) }} onSave={saveCurrent} />
  return <SeasonList seasons={seasons} onOpen={openSeason} onNew={() => setView('setup')} />
}

function SeasonList({ seasons, onOpen, onNew }) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <span style={{ fontSize: 22 }}>📅</span>
        <h3 className="font-bold text-xl">Season</h3>
      </div>
      <p className="text-base text-gray-700 mb-4">Play a Team Stableford or Team Cup season — points accumulate across every round to a running standings and a season pot.</p>
      <button className="btn-primary" style={{ backgroundColor: GREEN }} onClick={onNew}>+ New Season</button>
      {seasons.length === 0
        ? <p className="text-base text-gray-500 mt-4">No seasons yet. Start one — it accumulates points across every round.</p>
        : (
          <div className="mt-4 space-y-2">
            {seasons.map((s) => (
              <button key={s.id} onClick={() => onOpen(s.id)} className="w-full text-left rounded-lg border p-3" style={{ background: '#fff', ...PANEL }}>
                <div className="flex items-center gap-2 text-lg font-bold text-gray-800">
                  {s.seasonType === 'team-cup' ? '🏆' : '🎯'} {s.name || 'Season'}
                </div>
                <p className="text-sm text-gray-600 mt-1">{s.seasonType === 'team-cup' ? 'Team Cup Season' : 'Team Stableford Season'}</p>
              </button>
            ))}
          </div>
        )}
    </div>
  )
}

function SeasonSetup({ onCancel, onCreated }) {
  const [type, setType] = useState('team-stableford')
  const [name, setName] = useState('')
  const [teams, setTeams] = useState([blankTeam(1), blankTeam(2)])
  const [sides, setSides] = useState([])
  const [pot, setPot] = useState({ total: '', payout: 'winner', n: 3, useSides: false })
  const [sideBets, setSideBets] = useState({ scats: false, skins: false, dots: false })
  const [targetRounds, setTargetRounds] = useState('')
  const [busy, setBusy] = useState(false)

  const setTeam = (i, patch) => setTeams((t) => t.map((x, k) => (k === i ? { ...x, ...patch } : x)))
  const setPlayer = (ti, pi, patch) => setTeam(ti, { players: teams[ti].players.map((p, k) => (k === pi ? { ...p, ...patch } : p)) })
  const addTeam = () => setTeams((t) => [...t, blankTeam(t.length + 1)])
  const removeTeam = (i) => setTeams((t) => (t.length > 2 ? t.filter((_, k) => k !== i) : t))

  const addSide = () => setSides((s) => [...s, { sideId: nid('s'), name: `Side ${s.length + 1}`, teamIds: [] }])
  const toggleSideTeam = (si, teamId) => setSides((s) => s.map((x, k) => {
    if (k !== si) return x
    const has = x.teamIds.includes(teamId)
    return { ...x, teamIds: has ? x.teamIds.filter((id) => id !== teamId) : [...x.teamIds, teamId] }
  }))

  const create = async () => {
    setBusy(true)
    try {
      const meta = {
        name: name.trim() || (type === 'team-cup' ? 'Team Cup Season' : 'Team Stableford Season'),
        seasonType: type, net: true,
        teams: teams.map((t) => ({ id: t.id, name: t.name || t.id, players: t.players.map((p) => ({ playerId: p.playerId, name: p.name, ch: Number(p.ch) || 0 })) })),
        sides: type === 'team-stableford' ? sides.filter((s) => s.teamIds.length) : [],
        pot: { total: Number(pot.total) || 0, payout: pot.payout, n: Number(pot.n) || 3, useSides: !!pot.useSides },
        sideBets, targetRounds: Number(targetRounds) || 0, rounds: [],
      }
      const id = await saveMemberSeason(meta)
      onCreated(id)
    } finally { setBusy(false) }
  }

  const removeBtn = { background: '#fff', border: '1px solid #9AA0A6', borderRadius: 6, color: '#000', fontWeight: 900, fontSize: 14, lineHeight: 1, padding: '5px 9px' }

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <span style={{ fontSize: 22 }}>📅</span>
        <h3 className="font-bold text-xl">New Season</h3>
      </div>
      <div className="space-y-5">
        {/* choose season game */}
        <div>
          <div className={`${SUBLABEL} mb-2`}>Choose your season game</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {SEASON_TYPES.map((t) => (
              <button key={t.key} type="button" onClick={() => setType(t.key)} className="rounded-lg border p-3 text-left w-full"
                style={{ background: '#fff', borderColor: type === t.key ? '#1B7A3E' : '#E5E7EB' }}>
                <div className="flex items-center gap-2 text-lg font-bold text-gray-800">{t.icon} {t.label}</div>
                <p className="text-sm text-gray-600 mt-1">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* season name */}
        <div className="flex flex-wrap items-center gap-2">
          <input className={`${INP} flex-1 min-w-[200px]`} placeholder="Season name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        {/* teams */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className={SUBLABEL}>2-Man Teams</div>
            <button className="btn-ghost text-sm" onClick={addTeam}>+ Add Team</button>
          </div>
          <div className="space-y-2">
            {teams.map((t, ti) => (
              <div key={t.id} className="rounded-lg border p-3" style={{ background: '#fff', borderColor: '#D8DED8' }}>
                <div className="flex items-center gap-2 mb-2">
                  <input className={`${INPC} flex-1`} value={t.name} onChange={(e) => setTeam(ti, { name: e.target.value })} placeholder={`Team ${ti + 1} name`} />
                  <button type="button" title="Remove team" disabled={teams.length <= 2} style={{ ...removeBtn, color: teams.length > 2 ? '#000' : '#ccc' }} onClick={() => removeTeam(ti)}>✕</button>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {t.players.map((p, pi) => (
                    <div key={p.playerId} className="flex items-center gap-2">
                      <input className={`${INPC} flex-1`} value={p.name} onChange={(e) => setPlayer(ti, pi, { name: e.target.value })} placeholder={`Player ${pi + 1}`} />
                      <input className={`${INPC} w-20`} value={p.ch} onChange={(e) => setPlayer(ti, pi, { ch: e.target.value })} placeholder="CH" inputMode="numeric" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* sides (team stableford only) */}
        {type === 'team-stableford' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className={SUBLABEL}>Sides (optional — combine teams, e.g. 3 vs 3)</div>
              <button className="btn-ghost text-sm" onClick={addSide}>+ Add Side</button>
            </div>
            {sides.length === 0
              ? <p className="text-sm text-gray-500">Leave empty to rank by individual teams.</p>
              : sides.map((s, si) => (
                <div key={s.sideId} className="rounded-lg border p-3 mb-2" style={{ background: '#fff', borderColor: '#D8DED8' }}>
                  <input className={`${INPC} mb-2`} style={{ maxWidth: 220 }} value={s.name}
                    onChange={(e) => setSides((x) => x.map((y, k) => (k === si ? { ...y, name: e.target.value } : y)))} />
                  <div className="flex flex-wrap gap-2">
                    {teams.map((t) => {
                      const on = s.teamIds.includes(t.id)
                      return (
                        <button key={t.id} type="button" onClick={() => toggleSideTeam(si, t.id)}
                          style={{ padding: '8px 16px', borderRadius: 10, border: '2px solid', borderColor: on ? '#1B7A3E' : '#9AA0A6', background: on ? '#1B7A3E' : '#fff', color: on ? '#fff' : '#0F2914', fontWeight: 800, fontSize: 15 }}>
                          {on ? '✓ ' : ''}{t.name}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* season settings */}
        <div className="rounded-lg border p-3" style={PANEL}>
          <div className={`${SUBLABEL} mb-3`}>Season settings</div>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <label className="flex flex-col gap-1 text-lg font-semibold text-gray-800">Season pot ($)
              <input className={`${INPC} w-28`} value={pot.total} onChange={(e) => setPot({ ...pot, total: e.target.value })} inputMode="numeric" />
            </label>
            <label className="flex flex-col gap-1 text-lg font-semibold text-gray-800">Payout
              <select className={INPC} value={pot.payout} onChange={(e) => setPot({ ...pot, payout: e.target.value })}>
                <option value="winner">Winner-take-all</option>
                <option value="topN">Top-N split</option>
              </select>
            </label>
            {pot.payout === 'topN' && (
              <label className="flex flex-col gap-1 text-lg font-semibold text-gray-800">Pay top N
                <input className={`${INPC} w-20`} value={pot.n} onChange={(e) => setPot({ ...pot, n: e.target.value })} inputMode="numeric" />
              </label>
            )}
            <label className="flex flex-col gap-1 text-lg font-semibold text-gray-800">Target rounds (0 = open-ended)
              <input className={`${INPC} w-28`} value={targetRounds} onChange={(e) => setTargetRounds(e.target.value)} inputMode="numeric" />
            </label>
          </div>
          {type === 'team-stableford' && (
            <label className="flex items-center gap-2 mt-3 text-base font-semibold text-gray-700">
              <input type="checkbox" checked={pot.useSides} onChange={(e) => setPot({ ...pot, useSides: e.target.checked })} /> Rank the pot by SIDES (not individual teams)
            </label>
          )}
        </div>

        {/* side bets — icon check-to-expand rows, matching the game setups */}
        <div>
          <div className={`${SUBLABEL} mb-2`}>Side bets (optional — added each round)</div>
          <SideBetRows value={sideBets} onChange={setSideBets} />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button className="btn-primary" style={{ backgroundColor: GREEN }} disabled={busy} onClick={create}>{busy ? 'Creating…' : 'Create Season →'}</button>
          <button className="btn-ghost text-sm" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

function SeasonView({ season, onBack, onSave }) {
  const [adding, setAdding] = useState(false)
  const standings = useMemo(() => seasonStandings(season), [season])
  const pay = useMemo(() => potPayout(standings, season.pot), [standings, season.pot])
  const useSides = !!season.pot?.useSides && standings.sides.length > 0

  const saveRound = (round) => { onSave({ ...season, rounds: [...(season.rounds || []), round] }); setAdding(false) }
  const deleteRound = (i) => onSave({ ...season, rounds: season.rounds.filter((_, k) => k !== i) })

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span style={{ fontSize: 22 }}>{season.seasonType === 'team-cup' ? '🏆' : '🎯'}</span>
          <h3 className="font-bold text-xl">{season.name}</h3>
        </div>
        <button className="btn-ghost text-sm" onClick={onBack}>← All Seasons</button>
      </div>
      <p className="text-base text-gray-700 mb-4">
        {season.seasonType === 'team-cup' ? 'Team Cup Season' : 'Team Stableford Season'} · {(season.rounds || []).length} round(s)
        {season.targetRounds ? ` of ${season.targetRounds}` : ' · open-ended'}
      </p>

      <div className="space-y-5">
        <button className="btn-primary" style={{ backgroundColor: GREEN }} onClick={() => setAdding(true)}>+ Add Round</button>

        {adding && <RoundEditor season={season} onCancel={() => setAdding(false)} onSave={saveRound} />}

        {/* standings */}
        <div className="rounded-lg border p-3" style={PANEL}>
          <div className={`${SUBLABEL} mb-2`}>Season Standings{useSides ? ' — Sides' : ''}</div>
          <table className="w-full text-base">
            <tbody>
              {(useSides ? standings.sides : standings.teamRows).map((r) => (
                <tr key={r.teamId || r.sideId} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                  <td className="py-1.5 pr-2 font-bold text-gray-800" style={{ width: 28 }}>{r.place}</td>
                  <td className="py-1.5 text-gray-800">{r.name}</td>
                  <td className="py-1.5 text-right font-extrabold text-gray-900">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!useSides && standings.sides.length > 0 && (
            <p className="mt-2 text-sm text-gray-600">Sides: {standings.sides.map((s) => `${s.name} ${s.total}`).join(' · ')}</p>
          )}
        </div>

        {/* pot */}
        {season.pot?.total > 0 && (
          <div className="rounded-lg border p-3" style={PANEL}>
            <div className={`${SUBLABEL} mb-2`}>Season Pot — ${season.pot.total}</div>
            <p className="text-base text-gray-800">
              {pay.payouts.length ? pay.payouts.map((p) => `${p.name}: $${p.amount}`).join(' · ') : 'No scores entered yet.'}
            </p>
          </div>
        )}

        {/* rounds */}
        {(season.rounds || []).length > 0 && (
          <div>
            <div className={`${SUBLABEL} mb-2`}>Rounds</div>
            <div className="space-y-2">
              {season.rounds.map((rd, i) => (
                <div key={rd.roundId || i} className="flex items-center justify-between rounded-lg border p-3" style={{ background: '#fff', ...PANEL }}>
                  <span className="text-base text-gray-800">
                    Round {i + 1}{rd.courseName ? ` · ${rd.courseName}` : ''}
                    {Object.values(rd.sideBets || {}).some(Boolean) ? ` · side bets: ${Object.entries(rd.sideBets).filter(([, v]) => v).map(([k]) => k).join('/')}` : ''}
                  </span>
                  <button type="button" style={{ background: '#fff', border: '1px solid #9AA0A6', borderRadius: 6, color: '#B42318', fontWeight: 900, fontSize: 14, lineHeight: 1, padding: '5px 9px' }} onClick={() => deleteRound(i)}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function RoundEditor({ season, onCancel, onSave }) {
  const players = useMemo(() => (season.teams || []).flatMap((t) => t.players.map((p) => ({ ...p, teamName: t.name }))), [season])
  const [courseName, setCourseName] = useState('')
  const [scores, setScores] = useState({})
  const [sideBets, setSideBets] = useState(season.sideBets || { scats: false, skins: false, dots: false })

  const setScore = (pid, hole, val) => setScores((s) => ({ ...s, [pid]: { ...(s[pid] || {}), [hole]: val } }))

  const save = () => {
    const scoresByHole = {}
    for (let h = 1; h <= 18; h++) {
      const row = {}
      for (const p of players) {
        const v = Number(scores[p.playerId]?.[h])
        if (Number.isFinite(v) && v > 0) row[p.playerId] = v
      }
      if (Object.keys(row).length) scoresByHole[h] = row
    }
    onSave({ roundId: nid('r'), date: Date.now(), courseName: courseName.trim(), net: season.net !== false, pars: DEFAULT_PARS, scoresByHole, sideBets })
  }

  const cell = { padding: '4px 6px', borderBottom: '1px solid #eee' }

  return (
    <div className="rounded-lg border p-3" style={{ background: '#fff', ...PANEL }}>
      <div className={`${SUBLABEL} mb-3`}>New Round</div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input className={`${INPC} min-w-[200px]`} placeholder="Course (optional)" value={courseName} onChange={(e) => setCourseName(e.target.value)} />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="text-base" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f6f6f2', color: '#555' }}>
              <th style={{ ...cell, textAlign: 'left', position: 'sticky', left: 0, background: '#f6f6f2' }}>Player</th>
              {Array.from({ length: 18 }, (_, i) => <th key={i} style={{ ...cell, textAlign: 'center' }}>{i + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.playerId}>
                <td style={{ ...cell, position: 'sticky', left: 0, background: '#fff', whiteSpace: 'nowrap', fontWeight: 700, color: '#1f2937' }}>
                  {p.name || 'Player'} <span className="text-gray-500" style={{ fontWeight: 400 }}>({p.teamName})</span>
                </td>
                {Array.from({ length: 18 }, (_, i) => {
                  const h = i + 1
                  return (
                    <td key={h} style={cell}>
                      <input value={scores[p.playerId]?.[h] ?? ''} onChange={(e) => setScore(p.playerId, h, e.target.value)}
                        inputMode="numeric" className="border rounded text-center text-base" style={{ width: 42, padding: '5px 2px' }} />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <div className={`${SUBLABEL} mb-2`}>Side bets this round</div>
        <SideBetRows value={sideBets} onChange={setSideBets} />
      </div>
      <div className="flex items-center gap-2 pt-3">
        <button className="btn-primary" style={{ backgroundColor: GREEN }} onClick={save}>Save Round</button>
        <button className="btn-ghost text-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}
